import React from 'react';
import { Icon } from '@shoflo-rundown/polymer.legacy';

export const addOrDeleteGlobalColumnWarning = (
  <div>
    <span>
      Column arrangement in the Global Elements Manager is very important and
      making changes can have major impacts in the way your global elements
      publish across shows.
    </span>
    <br />
    <br />
    <span>
      It is important that the quantity of columns in Element Manager match your
      target show's column quantity. If you add or delete a column in the
      Element Manager, you need to add or remove corresponding columns in your
      target shows so that when you publish global element changes, they
      correctly display in your shows.
    </span>
    <br />
    <br />
    <span>
      Learn more about{' '}
      <a
        rel="noopener noreferrer"
        target="_blank"
        href="https://intercom.help/shoflo/global-elements-managing-columns"
      >
        Global Element Columns
      </a>{' '}
      or use the blue support chat on the bottom right of your screen.
    </span>
    <br />
  </div>
);

export const globalElementColumnDiscrepancyWarning = (
  <div>
    <b>Heads up!</b> This show contains Global Elements. Please be mindful that
    your columns continue to match up with your Global Element Manager.{' '}
    <a
      target="_blank"
      href="http://learn.shoflo.tv/global-element-basics/global-elements-managing-columns"
    >
      Learn more here
    </a>
  </div>
);

export const publishShowRowChangesToGlobalElementManager = (
  <div>
    <p>
      This will publish the current row to the Global Element Manager. Are you
      sure?
    </p>
  </div>
);

export const publishAllShowRowChangesToGlobalElementManager = (
  <div>
    <p>
      Any changes made to individual Global Elements in this local show will be
      published to the Global Element Manager. This is a bulk update and will
      apply to all Global Elements in this show. Are you sure?
    </p>
  </div>
);

export const retrieveLatestGlobalElementChanges = (
  <div>
    <p>
      Any local changes made to this element in this show including additional
      text, formatting and times will be replaced by the contents of the Global
      Element.
    </p>
    <p>
      Learn more about{' '}
      <a href="https://intercom.help/shoflo/global-elements" target="_blank">
        Global Elements
      </a>{' '}
      or use the blue support chat.
    </p>
  </div>
);

export const globalElementFunctionDisabled =
  'This function is disabled while in ' + ' a filtered tag or category.';

export const deleteGlobalElementSet =
  "You're about to delete an element set which will delete all of its " +
  'corresponding global elements.  Any global elements from this element set that ' +
  'you have inside of shows will be converted to normal show rows.  This action ' +
  'cannot be undone!  Reach out to Shoflo support with any questions.';

export const upgradeGlobalElements =
  'Global Elements is not currently activated ' +
  'for your team.  To activate, please reach out to your account executive, or use ' +
  'the blue chat icon in the lower right to live-chat with us.';

export const upgradeTeleprompter =
  "We're pleased to introduce an all new teleprompter " +
  'view, designed to display your existing Shoflo content in a beautiful, fullscreen view. ' +
  'Perfect for routing through to a teleprompter, or displayed on a downstage monitor for ' +
  'the announcer to see.';

export const welcomeTeleprompter =
  'A Shoflo view designed for speaker notes & scripts. ' +
  'Seamlessly make edits directly inline, track the Show Caller, auto scroll your ' +
  'scripts and more.';

export const deleteRow = 'Are you sure you want to remove this item row?';

export const deleteGlobalElementRowFromShow =
  "You're about to delete a Global Element. " +
  'This will remove the element from your show, but can still be accessed via ' +
  'the element manager.';

export const deleteGlobalElementRow =
  "You're about to delete a Global Element. " +
  'It will be removed from the element manager but will remain in your shows ' +
  'as a regular row.';

export const deleteRows = 'Are you sure you want to remove these item rows?';

export const deleteGlobalElementRowsFromShow =
  "You're about to delete a Global Element. " +
  'This will remove the element(s) from your show, but can still be accessed via ' +
  'the element manager.';

export const bulkWarning =
  'You are about to apply a bulk action, which will impact ' +
  'any users currently working in this show. This bulk action will not capture ' +
  "any users current edits being made to a cell. This can't be undone. Are you ready?";

export const bulkDelete = (rowsSelected) => (
  <p>
    This will bulk delete the <strong>{rowsSelected} rows</strong> you have
    selected. You cannot undo a bulk action. You will be able to undo this
    action from activity feed on a per row basis
  </p>
);

export const readOnlyPastEventAlertBar =
  'This is a Past Event & is in READ ONLY MODE.';

export const readOnlyPastEventModal =
  'This event is in read only mode because it is a past ' +
  'event. You can still export to csv, xls & pdf. If you are in subscription you ' +
  'can duplicate this event and start editing. You can reach out via our support ' +
  'chat below or email info@shoflo.tv with more questions.';

export const readOnlyDeletedEventAlertBar =
  'This is a trashed event & is in READ ONLY MODE.';

export const readOnlyDeletedEventModal =
  'This event is in read only mode because it is a ' + 'trashed show';

export const readOnlySavedShowAlertBar =
  'This is a saved show & is in READ ONLY MODE';

export const readOnlySavedShowModal =
  'This show is in read-only mode because it is a ' +
  'saved show. You can still export to csv, xls & pdf. You can also copy & paste ' +
  'rows from this show to other active shows. You can restore this show from the event ' +
  'dashboard. Reach out via our support chat below or email info@shoflo.tv ' +
  'with more questions.';

export const readOnlyDeletedShowAlertBar =
  'This is an Archived Show & is in READ ONLY MODE.';

export const readOnlyDeletedShowModal =
  'This show is in read only mode because it is a ' + 'trashed show';

export const readOnlySocketDisconnectedModal =
  "Your device has lost it's connection to Shoflo. " +
  "This is likely related to your network having trouble connecting to the internet. We've " +
  'disabled editing while we continue trying to reconnect. ';

/**
 * TODO: remove this or change this language, as we now support other column types
 */
export const specialCellSelectionsModal =
  'Per cell copy & paste is currently ' +
  'supported on contributor columns only. We will be supporting copy & paste ' +
  'for my notes & item fields soon. Tip: You can copy & paste ' +
  'a full row easily by going into row edit mode.';

export const lockedRowModal =
  "This row (or a row in your selection) is currently locked which means you can't make " +
  'edits to it. If you are an Admin or Show Caller, you can unlock this row by ' +
  'clicking the "lock" icon located above the item #.';

export const lockRowLocked =
  'Your row is now LOCKED... no one will be able to edit until you ' +
  '(another admin / show caller) unlocks it. Your crew can still add personal highlights & ' +
  'private notes. Just a simple safety lock ;)';

export const lockRowUnlocked =
  'Your row is now UNLOCKED which means your crew can start ' +
  'making edits based on their permission. You can always lock it again later. ' +
  "And don't forget, you can always undo :)";

export const cutRowTransferActivityFeedWarning =
  "When cutting a row, it doesn't include cell edit history.";

export const deleteLastItemRow =
  "You can't delete the last row because then you would not have a Shoflo...";

export const cutLastItemRow =
  "You can't cut the last row because then you would not have a Shoflo...";

export const goToDeletedEvent =
  'You must restore a deleted event before it can be viewed.';

export const errorViewingDeletedEvent =
  'You must restore a deleted event before it can be viewed.';

export const pasteShapeError =
  'The information cannot be pasted because the copy ' +
  'area and the paste area are not the same size and shape';

export const pastePermissionError =
  'Your permission level does not allow you to make edits here.';

export const copyOrCutInHeaderCellWarning =
  'Column header copy, cut, and paste is not currently supported.';

export const autoCountCuesWarning =
  'This will reset all of your item numbers and they ' +
  'will now re-count as you add, remove and move items around. You will ' +
  'lose any manually set item numbers (106.7 etc). Be very sure before you ' +
  'do this. Reach out to support chat with more questions. Are you sure?';

export const showLoadFailedMessage =
  'Oh man! There was a problem loading this show. Please contact the Shoflo support team.';

export const showLoadFailedForbiddenMessage =
  'You don’t have permission to view this show.  Reach out to an ' +
  'admin on the event and have them send you an invite to gain access.';

export const showLoadFailed404Message =
  'Oh man! It looks like this was deleted or never existed.';

export const deletedShowMustBeRestoredMessage =
  'You must restore a deleted show before it can be viewed.';

export const remotePrompterModalDesc1 =
  'Remote prompter monitor will display the prompter cleanly for the ' +
  'presenter to read from. You can launch the monitor full screen on a ' +
  'secondary display output or you can share the link for a remote ' +
  'presenter to read from any modern web browser';

export const remotePrompterModalDesc2 =
  'You will be remotely controlling the scroll position, scroll ' +
  'speed and text styling of this remote prompter link';

export const guestPassDisabled =
  'Guest pass is disabled for this url. ' +
  'This show/schedule/list has been deleted and the guest pass access is no longer available. Please reach out to your event admin for more information.';

export const guestPassEventDescription = (
  <p>
    Guest Passes are special view-only versions of your shows that can be
    accessed by anyone with the link. You can disable access to the event guest
    pass landing page by clicking on the first switch. You can also toggle
    access to each guest pass by toggling the switch next to it.
  </p>
);

export const guestPassGlobalElementDescription = (
  <p>
    Guest Passes are special view-only versions of your element sets that can be
    accessed by anyone with the link. You can disable access to the Global
    Element Manager guest pass landing page by clicking on the first switch. You
    can also toggle access to each guest pass by toggling the switch next to it.
  </p>
);

export const guestPassShowDescription = (
  <p>
    Guest Passes are special view-only versions of your shows that can be
    accessed by anyone with the link. You can toggle access to this guest pass
    by clicking on the first switch. Set your column order and column visibility
    for this guest pass via "Set Visible Columns".
  </p>
);

export const eventPermissionsChangedWarning =
  'Your event permission level has changed for this event.';

export const copyPastingTimingCellsModal =
  'Pasting in timing column cells is not currently supported.';

export const teleprompterOnboardingColumnSelect =
  'You can load any column ' +
  'from your show into Prompter. When you make changes in Prompter View, ' +
  'they are seen instantly for all users and all views.';

export const teleprompterOnboardingRowSelect = (
  <span>
    Select an item from the left pane to see its content displayed in the
    prompter area in the middle of your screen. Rows with a pilcrow icon (
    <Icon name="paragraph" />) contain content in that row in your selected
    column.
  </span>
);

export default {
  teleprompterOnboardingColumnSelect,
  teleprompterOnboardingRowSelect,
  addOrDeleteGlobalColumnWarning,
  deletedShowMustBeRestoredMessage,
  autoCountCuesWarning,
  pasteShapeError,
  errorViewingDeletedEvent,
  goToDeletedEvent,
  deleteLastItemRow,
  cutLastItemRow,
  bulkWarning,
  bulkDelete,
  specialCellSelectionsModal,
  lockedRowModal,
  copyPastingTimingCellsModal,
  readOnlyPastEventAlertBar,
  readOnlyPastEventModal,
  readOnlyDeletedEventAlertBar,
  readOnlyDeletedEventModal,
  readOnlySavedShowAlertBar,
  readOnlySavedShowModal,
  readOnlyDeletedShowAlertBar,
  readOnlyDeletedShowModal,
  readOnlySocketDisconnectedModal,
  lockRowLocked,
  lockRowUnlocked,
  cutRowTransferActivityFeedWarning,
  pastePermissionError,
  copyOrCutInHeaderCellWarning,
  eventPermissionsChangedWarning,
  showLoadFailedMessage,
  showLoadFailed404Message,
  deleteRow,
  deleteGlobalElementRowFromShow,
  deleteGlobalElementRow,
  deleteRows,
  deleteGlobalElementRowsFromShow,
  upgradeGlobalElements,
  upgradeTeleprompter,
  welcomeTeleprompter,
  showLoadFailedForbiddenMessage,
  guestPassDisabled,
  retrieveLatestGlobalElementChanges,
  globalElementFunctionDisabled,
  deleteGlobalElementSet,
  globalElementColumnDiscrepancyWarning,
  publishShowRowChangesToGlobalElementManager,
  publishAllShowRowChangesToGlobalElementManager,
  guestPassEventDescription,
  guestPassShowDescription,
  guestPassGlobalElementDescription,
};
